import { Message, Channel, TypingUser, UserOnlineStatus, ChannelType } from '@/types/chat';
import { chatWebSocket } from '@/lib/chat-websocket-mock';

const MESSAGES_KEY = 'venntix_chat_messages';
const CHANNELS_KEY = 'venntix_chat_channels';
const UNREAD_KEY = 'venntix_chat_unread';

/**
 * Get all messages for a channel
 */
export function getMessages(channelId: string): Message[] {
    if (typeof window === 'undefined') return [];

    const stored = localStorage.getItem(`${MESSAGES_KEY}_${channelId}`);
    if (!stored) return [];

    try {
        const messages = JSON.parse(stored);
        return messages.filter((m: Message) => !m.deleted);
    } catch {
        return [];
    }
}

/**
 * Send a new message
 */
export function sendMessage(message: Omit<Message, 'id' | 'timestamp'>): Message {
    const newMessage: Message = {
        ...message,
        id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        timestamp: new Date().toISOString(),
    };

    // Save to localStorage
    const messages = getMessages(message.channelId);
    messages.push(newMessage);
    localStorage.setItem(`${MESSAGES_KEY}_${message.channelId}`, JSON.stringify(messages));

    // Broadcast to other tabs
    chatWebSocket.broadcast('new_message', newMessage);

    // Update channel's last message
    updateChannelLastMessage(message.channelId, newMessage);

    return newMessage;
}

/**
 * Delete a message
 */
export function deleteMessage(messageId: string, channelId: string, userId: string): boolean {
    const messages = getMessages(channelId);
    const message = messages.find(m => m.id === messageId);

    if (!message || message.userId !== userId) return false;

    message.deleted = true;
    localStorage.setItem(`${MESSAGES_KEY}_${channelId}`, JSON.stringify(messages));

    chatWebSocket.broadcast('message_deleted', { messageId, channelId });

    return true;
}

/**
 * Edit a message
 */
export function editMessage(messageId: string, channelId: string, userId: string, newContent: string): boolean {
    const messages = getMessages(channelId);
    const message = messages.find(m => m.id === messageId);

    if (!message || message.userId !== userId) return false;

    message.content = newContent;
    message.edited = true;
    message.editedAt = new Date().toISOString();

    localStorage.setItem(`${MESSAGES_KEY}_${channelId}`, JSON.stringify(messages));

    chatWebSocket.broadcast('message_edited', { messageId, channelId, newContent });

    return true;
}

/**
 * Get all channels
 */
export function getAllChannels(): Channel[] {
    if (typeof window === 'undefined') return [];

    const stored = localStorage.getItem(CHANNELS_KEY);
    if (!stored) {
        // Create default global channels
        const defaultChannels = createDefaultChannels();
        localStorage.setItem(CHANNELS_KEY, JSON.stringify(defaultChannels));
        return defaultChannels;
    }

    try {
        return JSON.parse(stored);
    } catch {
        return [];
    }
}

/**
 * Create default global channels
 */
function createDefaultChannels(): Channel[] {
    return [
        {
            id: 'global_all',
            name: 'All',
            type: 'global',
            isPrivate: false,
            members: [],
            moderators: [],
            unreadCount: 0,
            createdAt: new Date().toISOString(),
        },
        {
            id: 'global_esport',
            name: 'Esports',
            type: 'global',
            category: 'Esport',
            isPrivate: false,
            members: [],
            moderators: [],
            unreadCount: 0,
            createdAt: new Date().toISOString(),
        },
        {
            id: 'global_seminar',
            name: 'Seminar',
            type: 'global',
            category: 'Seminar',
            isPrivate: false,
            members: [],
            moderators: [],
            unreadCount: 0,
            createdAt: new Date().toISOString(),
        },
        {
            id: 'global_workshop',
            name: 'Workshop',
            type: 'global',
            category: 'Workshop',
            isPrivate: false,
            members: [],
            moderators: [],
            unreadCount: 0,
            createdAt: new Date().toISOString(),
        },
    ];
}

/**
 * Get channel by ID
 */
export function getChannelById(channelId: string): Channel | undefined {
    const channels = getAllChannels();
    return channels.find(c => c.id === channelId);
}

/**
 * Update channel's last message
 */
function updateChannelLastMessage(channelId: string, message: Message) {
    const channels = getAllChannels();
    const channel = channels.find(c => c.id === channelId);

    if (channel) {
        channel.lastMessage = message;
        localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
    }
}

/**
 * Get unread count for channel
 */
export function getUnreadCount(userId: string, channelId: string): number {
    if (typeof window === 'undefined') return 0;

    const key = `${UNREAD_KEY}_${userId}_${channelId}`;
    const stored = localStorage.getItem(key);

    return stored ? parseInt(stored, 10) : 0;
}

/**
 * Mark channel as read
 */
export function markAsRead(userId: string, channelId: string) {
    if (typeof window === 'undefined') return;

    const key = `${UNREAD_KEY}_${userId}_${channelId}`;
    localStorage.setItem(key, '0');

    // Update channel unread count
    const channels = getAllChannels();
    const channel = channels.find(c => c.id === channelId);
    if (channel) {
        channel.unreadCount = 0;
        localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
    }
}

/**
 * Increment unread count
 */
export function incrementUnread(userId: string, channelId: string) {
    if (typeof window === 'undefined') return;

    const current = getUnreadCount(userId, channelId);
    const key = `${UNREAD_KEY}_${userId}_${channelId}`;
    localStorage.setItem(key, (current + 1).toString());

    // Update channel unread count
    const channels = getAllChannels();
    const channel = channels.find(c => c.id === channelId);
    if (channel) {
        channel.unreadCount = current + 1;
        localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
    }
}

/**
 * Get total unread count for user
 */
export function getTotalUnreadCount(userId: string): number {
    if (typeof window === 'undefined') return 0;

    const channels = getAllChannels();
    let total = 0;

    channels.forEach(channel => {
        total += getUnreadCount(userId, channel.id);
    });

    return total;
}

/**
 * Create team channel
 */
export function createTeamChannel(teamId: string, teamName: string, members: string[]): Channel {
    const channels = getAllChannels();

    const newChannel: Channel = {
        id: `team_${teamId}_general`,
        name: `${teamName} - General`,
        type: 'team',
        teamId,
        isPrivate: true,
        members,
        moderators: [members[0]], // First member is captain
        unreadCount: 0,
        createdAt: new Date().toISOString(),
    };

    channels.push(newChannel);
    localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));

    return newChannel;
}

/**
 * Create match channel
 */
export function createMatchChannel(matchId: string, team1Members: string[], team2Members: string[]): Channel {
    const channels = getAllChannels();

    const newChannel: Channel = {
        id: `match_${matchId}`,
        name: `Match Lobby`,
        type: 'match',
        matchId,
        isPrivate: true,
        members: [...team1Members, ...team2Members],
        moderators: [],
        unreadCount: 0,
        createdAt: new Date().toISOString(),
    };

    channels.push(newChannel);
    localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));

    return newChannel;
}
